import { useEffect, useState } from 'react';
import { useLocation, useSearch } from 'wouter';
import { MessageCircle, ShieldCheck, Send, AlertCircle } from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const ERROR_MESSAGES: Record<string, string> = {
  missing_token: 'No recibimos ningún código de acceso. Pedile al bot un nuevo link.',
  invalid_or_expired:
    'Ese link ya fue usado o caducó. Pedile al bot uno nuevo (los links duran 15 minutos).',
  tenant_inactive: 'Tu cuenta no está activa. Hablá con tu contador.',
};

export function AppLoginPage() {
  const [, navigate] = useLocation();
  const search = useSearch();
  const params = new URLSearchParams(search);
  const errorKey = params.get('error') ?? undefined;
  const [checking, setChecking] = useState(true);

  // Si ya hay sesión, redirigir directo al panel
  useEffect(() => {
    let cancelled = false;
    api<{ authed: boolean; via?: string }>('/api/app/auth/status')
      .then((r) => {
        if (cancelled) return;
        if (r.authed) {
          navigate('/app');
        } else {
          setChecking(false);
        }
      })
      .catch(() => {
        if (!cancelled) setChecking(false);
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  if (checking) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Verificando sesión…</div>;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted px-4 py-8">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-3 flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            <ShieldCheck className="size-6" />
          </div>
          <CardTitle className="text-2xl">Tu panel de Actuo</CardTitle>
          <CardDescription>
            Por seguridad, el acceso al panel se hace por un link que te manda el bot a tu WhatsApp.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {errorKey && ERROR_MESSAGES[errorKey] && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              <AlertCircle className="size-4 mt-0.5 shrink-0" />
              <span>{ERROR_MESSAGES[errorKey]}</span>
            </div>
          )}

          <div className="space-y-3 text-sm">
            <Step
              icon={<MessageCircle className="size-4" />}
              n={1}
              title="Abrí tu chat con Actuo"
              detail="En el WhatsApp donde recibís las notificaciones."
            />
            <Step
              icon={<Send className="size-4" />}
              n={2}
              title="Mandá la palabra panel"
              detail='Escribí "panel" (o "quiero entrar a la web", el bot entiende).'
            />
            <Step
              icon={<ShieldCheck className="size-4" />}
              n={3}
              title="Abrí el link"
              detail="El bot te va a mandar una URL personal. Dura 15 minutos y se usa una sola vez."
            />
          </div>

          <p className="text-xs text-muted-foreground text-center pt-2 border-t border-border">
            ¿No tenés el bot configurado? Hablá con tu contador para que termine el onboarding.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function Step({
  icon,
  n,
  title,
  detail,
}: {
  icon: React.ReactNode;
  n: number;
  title: string;
  detail: string;
}) {
  return (
    <div className="flex items-start gap-3 rounded-md border border-border bg-muted/30 p-3">
      <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">
        {n}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium inline-flex items-center gap-1.5">
          {icon}
          {title}
        </div>
        <div className="text-xs text-muted-foreground mt-0.5">{detail}</div>
      </div>
    </div>
  );
}
